import { Button, message, Popover } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FC, useCallback, useState } from 'react';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { fetchAppointmentPaymentInfo } from 'services/api/appointments';
import { AppointmentPaymentInfoResult } from 'types/api/appointments';

import { PaymentDetails } from 'components/shared';

interface PaymentInfoProps {
  appointmentId: string;
}

const PaymentInfo: FC<PaymentInfoProps> = ({ appointmentId }) => {
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [paymentData, setPaymentData] =
    useState<AppointmentPaymentInfoResult | null>(null);

  const stopLoading = () => {
    setLoading(false);
    setTimeout(() => {
      setRefetching(false);
    }, 500);
  };

  const fetchPayment = useCallback(async () => {
    try {
      const data = await fetchAppointmentPaymentInfo(appointmentId);
      setPaymentData(data);
      stopLoading();

      if (!popoverVisible) {
        setPopoverVisible(true);
      }
    } catch (err) {
      stopLoading();
      message.error(
        `Failed to load payment information: ${
          (err as Error)?.message ?? 'unknown'
        }`
      );
    }
  }, [appointmentId, popoverVisible]);

  const onClick = () => {
    if (popoverVisible) {
      setPopoverVisible(false);

      return;
    }

    if (paymentData) {
      setPopoverVisible(true);

      return;
    }

    setLoading(true);
    fetchPayment();
  };

  const handlePopoverVisibleChange = (visible: boolean) => {
    if (!visible) {
      setPopoverVisible(false);
    }
  };

  const handleRefetch = () => {
    setRefetching(true);
    fetchPayment();
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format(FULL_DATE_FORMAT);
  };

  const renderPopoverContent = () => (
    <div
      style={{
        maxWidth: '500px',
        minWidth: '380px',
        maxHeight: '400px',
        overflow: 'auto'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}
      >
        <span style={{ fontSize: '12px', color: '#666' }}>
          Last updated: {formatDate(new Date().toISOString())}
        </span>
        <Button
          type="text"
          size="middle"
          icon={<ReloadOutlined spin={refetching} />}
          onClick={handleRefetch}
          loading={refetching}
          style={{ padding: '4px 8px' }}
        >
          Refresh
        </Button>
      </div>

      <PaymentDetails payments={paymentData?.patient_payment_info || []} />
    </div>
  );

  return (
    <Popover
      content={renderPopoverContent()}
      title="Payment Information"
      trigger="click"
      open={popoverVisible}
      placement="rightTop"
      onOpenChange={handlePopoverVisibleChange}
      overlayStyle={{ maxWidth: '600px' }}
    >
      <Button
        loading={loading}
        type="default"
        size="small"
        icon={<InfoCircleOutlined />}
        onClick={onClick}
      >
        View Payment Info
      </Button>
    </Popover>
  );
};

export default PaymentInfo;
